export type CartLine = {
  catalogItemId: number
  name: string
  privateSupplierId: number
  privateSupplierName: string
  quantity: number
  unit: string
  unitPrice: number | null
  note: string | null
  brand: string | null
}

type CartState = {
  lines: CartLine[]
}

const STORAGE_KEY = 'provisium.miniapp.cart.v1'

const listeners = new Set<() => void>()

const load = (): CartState => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return { lines: [] }
    }
    const parsed = JSON.parse(raw) as CartState
    if (!Array.isArray(parsed.lines)) {
      return { lines: [] }
    }
    return { lines: parsed.lines }
  } catch {
    return { lines: [] }
  }
}

let state: CartState = load()

const persist = () => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // localStorage может быть недоступен в WebView — корзина останется в памяти сессии.
  }
  listeners.forEach((listener) => listener())
}

export const getCartLines = (): CartLine[] => state.lines

export const getCartCount = (): number =>
  state.lines.reduce((sum, line) => sum + line.quantity, 0)

export const subscribeCart = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export const addToCart = (item: Omit<CartLine, 'quantity'> & { quantity?: number }) => {
  const qty = item.quantity ?? 1
  const existing = state.lines.find((line) => line.catalogItemId === item.catalogItemId)
  if (existing) {
    state = {
      lines: state.lines.map((line) =>
        line.catalogItemId === item.catalogItemId
          ? { ...line, quantity: line.quantity + qty }
          : line,
      ),
    }
  } else {
    state = {
      lines: [
        ...state.lines,
        {
          catalogItemId: item.catalogItemId,
          name: item.name,
          privateSupplierId: item.privateSupplierId,
          privateSupplierName: item.privateSupplierName,
          quantity: qty,
          unit: item.unit,
          unitPrice: item.unitPrice,
          note: item.note,
          brand: item.brand,
        },
      ],
    }
  }
  persist()
}

export const setCartQuantity = (catalogItemId: number, quantity: number) => {
  if (quantity <= 0) {
    state = { lines: state.lines.filter((line) => line.catalogItemId !== catalogItemId) }
  } else {
    state = {
      lines: state.lines.map((line) =>
        line.catalogItemId === catalogItemId ? { ...line, quantity } : line,
      ),
    }
  }
  persist()
}

export const clearSupplierCart = (privateSupplierId: number) => {
  state = {
    lines: state.lines.filter((line) => line.privateSupplierId !== privateSupplierId),
  }
  persist()
}

export const clearCart = () => {
  state = { lines: [] }
  persist()
}

export const groupCartBySupplier = (lines: CartLine[] = state.lines) => {
  const map = new Map<number, { supplierId: number; supplierName: string; lines: CartLine[]; total: number }>()
  for (const line of lines) {
    const current = map.get(line.privateSupplierId)
    const lineTotal = (line.unitPrice ?? 0) * line.quantity
    if (current) {
      current.lines.push(line)
      current.total += lineTotal
    } else {
      map.set(line.privateSupplierId, {
        supplierId: line.privateSupplierId,
        supplierName: line.privateSupplierName,
        lines: [line],
        total: lineTotal,
      })
    }
  }
  return [...map.values()]
}
